"use client";

/**
 * ChatMock — an animated mock of the askFinz Chat surface for marketing pages.
 *
 * NOT the real ChatDrawer (which is 3000+ lines of product logic). This is a
 * pure-presentation component that mimics the look-and-feel of a chat run:
 * model picker, message stream, typing indicator, side-by-side compare. All
 * data is hard-coded; no network calls, no auth, no real models.
 *
 * Lives in askfinz.com only. Don't reuse on the product surfaces.
 */
import { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  Sparkles,
  Brain,
  Eye,
  Code2,
  ArrowUpRight,
  GitBranch,
  Send,
  Paperclip,
  AtSign,
  Hash,
} from "lucide-react";

type Provider = {
  id: string;
  label: string;
  category: "Frontier" | "Reasoning" | "Open-weight" | "Vision" | "Code";
  icon: typeof Sparkles;
};

const PROVIDERS: Provider[] = [
  { id: "p-frontier-fast",   label: "Frontier · fast",       category: "Frontier",     icon: Sparkles },
  { id: "p-frontier-deep",   label: "Frontier · reasoning",  category: "Reasoning",    icon: Brain },
  { id: "p-open-weight",     label: "Open-weight · 70B",     category: "Open-weight",  icon: Hash },
  { id: "p-vision",          label: "Vision-capable",        category: "Vision",       icon: Eye },
  { id: "p-code-tuned",      label: "Code-tuned",            category: "Code",         icon: Code2 },
];

type Message =
  | { kind: "user"; text: string; provider?: never }
  | { kind: "assistant"; provider: Provider; text: string }
  | { kind: "compare"; left: { provider: Provider; text: string }; right: { provider: Provider; text: string } };

const SCRIPT: Message[] = [
  {
    kind: "user",
    text: "Summarise the three most cited fairness frameworks for LLMs and tell me which is most actionable.",
  },
  {
    kind: "assistant",
    provider: PROVIDERS[0],
    text:
      "Three commonly-cited frameworks: NIST AI RMF (process, governance), the EU AI Act (risk-tiered, regulatory) and the FAIR data principles (technical, data-side). Most actionable for an applied team is NIST RMF — concrete controls map cleanly to existing release processes.",
  },
  {
    kind: "user",
    text: "Compare your answer with the reasoning model.",
  },
  {
    kind: "compare",
    left: {
      provider: PROVIDERS[0],
      text:
        "Same three. NIST is the most actionable: ships as worksheets and maps to standard release gates.",
    },
    right: {
      provider: PROVIDERS[1],
      text:
        "Worth distinguishing actionable-for-engineers (NIST) from actionable-for-policy (EU AI Act). For a small team with no compliance officer, NIST first; revisit EU at scale.",
    },
  },
];

// First-paint snapshot: render the first user message + first assistant reply
// already visible, so the mock never looks "empty/loading" on initial paint.
const INITIAL_STEP = 2;

export function ChatMock() {
  const [step, setStep] = useState(INITIAL_STEP);
  const [provider, setProvider] = useState<Provider>(PROVIDERS[0]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [typing, setTyping] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  // Drive the script when the mock first scrolls into view.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        const advance = (i: number) => {
          if (i >= SCRIPT.length) return;
          const next = SCRIPT[i];
          // Show typing indicator before each assistant or compare message
          if (next.kind === "assistant" || next.kind === "compare") {
            setTyping(true);
            setTimeout(() => {
              setTyping(false);
              setStep(i + 1);
              setTimeout(() => advance(i + 1), 800);
            }, 1100);
          } else {
            setStep(i + 1);
            setTimeout(() => advance(i + 1), 600);
          }
        };
        // initial pause, then pick up FROM the partial-fill state (don't replay
        // the messages already on screen).
        setTimeout(() => advance(INITIAL_STEP), 500);
        obs.disconnect();
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-3xl mx-auto">
      {/* Subtle ambient halo behind the frame */}
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            chat.askfinz.ai · live
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Demo
          </span>
        </div>

        {/* Conversation area */}
        <div className="px-5 py-5 min-h-[440px] flex flex-col gap-4">
          {SCRIPT.slice(0, step).map((m, i) => (
            <MessageBubble key={i} m={m} />
          ))}
          {typing && <TypingIndicator provider={provider} />}
          {step >= SCRIPT.length && (
            <div className="mt-2 self-start text-[11px] tracking-[0.16em] uppercase text-foreground/65 font-mono inline-flex items-center gap-2">
              <GitBranch className="h-3 w-3" />
              Side-by-side compare · keep the one that&#39;s right
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t hairline px-3 py-3 bg-background/60">
          <div className="flex items-end gap-2">
            <div className="relative">
              {/* Model picker */}
              <button
                type="button"
                onClick={() => setPickerOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 rounded-lg border hairline-strong px-2.5 py-1.5 text-[12px] hover:bg-foreground/5 transition"
              >
                <provider.icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{provider.label}</span>
                <span className="sm:hidden">{provider.category}</span>
                <ChevronDown className={"h-3 w-3 transition-transform " + (pickerOpen ? "rotate-180" : "")} />
              </button>
              {pickerOpen && (
                <div className="absolute bottom-full left-0 mb-1 z-10 w-56 rounded-xl border hairline-strong bg-card shadow-lg p-1">
                  {PROVIDERS.map((p) => {
                    const Icon = p.icon;
                    const active = p.id === provider.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setProvider(p);
                          setPickerOpen(false);
                        }}
                        className={
                          "w-full flex items-center gap-2 rounded-lg px-2.5 py-2 text-[12px] text-left transition " +
                          (active ? "bg-foreground text-background" : "hover:bg-foreground/5")
                        }
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{p.label}</div>
                          <div className={"text-[10px] " + (active ? "text-background/60" : "text-foreground/65")}>
                            {p.category}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Fake textarea */}
            <div className="flex-1 relative rounded-lg border hairline bg-background px-3 py-2 text-[13px] text-foreground/65 min-h-[40px]">
              <span className="opacity-60">Ask anything — agents will route between models as needed</span>
              <span className="cursor inline-block ml-1" aria-hidden />
            </div>

            <div className="flex items-center gap-1 text-foreground/65">
              <button type="button" className="p-1.5 rounded-md hover:bg-foreground/5 transition" aria-label="Attach">
                <Paperclip className="h-4 w-4" />
              </button>
              <button type="button" className="p-1.5 rounded-md hover:bg-foreground/5 transition" aria-label="Mention">
                <AtSign className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="p-1.5 rounded-md bg-foreground text-background hover:opacity-90 transition"
                aria-label="Send"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Below-frame caption */}
      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <ArrowUpRight className="h-3 w-3" />
        <span>Live product at <span className="font-mono text-foreground/65">chat.askfinz.ai</span></span>
      </div>
    </div>
  );
}

function MessageBubble({ m }: { m: Message }) {
  if (m.kind === "user") {
    return (
      <div className="self-end max-w-[75%] rounded-2xl rounded-br-md bg-foreground text-background px-4 py-2.5 text-[13px] leading-relaxed shadow-sm animate-[blur-fade-in_0.5s_ease-out]">
        {m.text}
      </div>
    );
  }
  if (m.kind === "assistant") {
    const Icon = m.provider.icon;
    return (
      <div className="self-start max-w-[80%] animate-[blur-fade-in_0.5s_ease-out]">
        <div className="flex items-center gap-1.5 mb-1 text-[10px] tracking-[0.14em] uppercase text-foreground/65">
          <Icon className="h-3 w-3" />
          {m.provider.label}
        </div>
        <div className="rounded-2xl rounded-tl-md border hairline bg-background px-4 py-2.5 text-[13px] leading-relaxed text-foreground/85">
          {m.text}
        </div>
      </div>
    );
  }
  // compare
  return (
    <div className="self-stretch grid grid-cols-1 md:grid-cols-2 gap-3 animate-[blur-fade-in_0.6s_ease-out]">
      {[m.left, m.right].map((side, i) => {
        const Icon = side.provider.icon;
        return (
          <div key={i} className="rounded-2xl border hairline bg-background p-3.5">
            <div className="flex items-center gap-1.5 mb-2 text-[10px] tracking-[0.14em] uppercase text-foreground/65">
              <Icon className="h-3 w-3" />
              {side.provider.label}
              {i === 0 && <span className="ml-auto text-[hsl(var(--accent))]/80">A</span>}
              {i === 1 && <span className="ml-auto text-foreground/65">B</span>}
            </div>
            <div className="text-[13px] leading-relaxed text-foreground/85">{side.text}</div>
          </div>
        );
      })}
    </div>
  );
}

function TypingIndicator({ provider }: { provider: Provider }) {
  const Icon = provider.icon;
  return (
    <div className="self-start animate-[blur-fade-in_0.4s_ease-out]">
      <div className="flex items-center gap-1.5 mb-1 text-[10px] tracking-[0.14em] uppercase text-foreground/65">
        <Icon className="h-3 w-3" />
        Thinking
      </div>
      <div className="rounded-2xl rounded-tl-md border hairline bg-background px-4 py-3 inline-flex items-center gap-1.5">
        <span className="h-1.5 w-1.5 rounded-full bg-foreground/35 animate-[typing-dot_1.4s_ease-in-out_infinite]" />
        <span className="h-1.5 w-1.5 rounded-full bg-foreground/35 animate-[typing-dot_1.4s_ease-in-out_infinite_0.2s]" />
        <span className="h-1.5 w-1.5 rounded-full bg-foreground/35 animate-[typing-dot_1.4s_ease-in-out_infinite_0.4s]" />
      </div>
    </div>
  );
}
